import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tabs,
  Tab,
  TextField,
  Button,
  MenuItem,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  Timeline as TimelineIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { AppDispatch, RootState } from '../store';
import { fetchUserProductivity, fetchProjectReports } from '../store/slices/reportSlice';
import { format, subDays, differenceInDays, parseISO } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { getGanttData, GanttTask } from '../services/reportService';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ReportsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { userProductivity = [], projectReports = [], loading = false } = useSelector((state: RootState) => state.reports || {});
  const [tabValue, setTabValue] = useState(0);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  useEffect(() => {
    dispatch(fetchUserProductivity({ startDate, endDate }));
    dispatch(fetchProjectReports());
  }, [dispatch, startDate, endDate]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleApplyDateFilter = () => {
    dispatch(fetchUserProductivity({ startDate, endDate }));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Reports & Analytics
        </Typography>
        <Button variant="outlined" startIcon={<DownloadIcon />}>
          Export PDF
        </Button>
      </Box>

      {/* Date Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              onClick={handleApplyDateFilter}
              fullWidth
            >
              Apply Filter
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="report tabs">
          <Tab icon={<PeopleIcon />} label="User Productivity" />
          <Tab icon={<AssessmentIcon />} label="Project Reports" />
          <Tab icon={<TimelineIcon />} label="Timeline" />
        </Tabs>

        {/* User Productivity Tab */}
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <LinearProgress />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell align="right">Tasks Completed</TableCell>
                    <TableCell align="right">Hours Logged</TableCell>
                    <TableCell align="right">Avg Time/Task</TableCell>
                    <TableCell>Productivity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userProductivity.map((user) => (
                    <TableRow key={user.userId}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {user.userName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">{user.tasksCompleted}</TableCell>
                      <TableCell align="right">{Math.round(user.totalHoursLogged)}h</TableCell>
                      <TableCell align="right">
                        {user.tasksCompleted > 0
                          ? Math.round((user.totalHoursLogged / user.tasksCompleted) * 10) / 10
                          : 0}h
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min((user.tasksCompleted / 10) * 100, 100)}
                            sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {Math.min(Math.round((user.tasksCompleted / 10) * 100), 100)}%
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {userProductivity.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary">
                          No data available for the selected period
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Project Reports Tab */}
        <TabPanel value={tabValue} index={1}>
          {loading ? (
            <LinearProgress />
          ) : (
            <Grid container spacing={3} sx={{ p: 2 }}>
              {projectReports.map((project) => (
                <Grid item xs={12} key={project.projectId}>
                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      {project.projectName}
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          Total Tasks
                        </Typography>
                        <Typography variant="h5">{project.totalTasks}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          Completed
                        </Typography>
                        <Typography variant="h5" color="success.main">
                          {project.completedTasks}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          In Progress
                        </Typography>
                        <Typography variant="h5" color="info.main">
                          {project.totalTasks - project.completedTasks}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          Overdue
                        </Typography>
                        <Typography variant="h5" color="error.main">
                          {project.overdueTasks}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Progress: {project.progressPercentage.toFixed(1)}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={project.progressPercentage}
                          sx={{ height: 10, borderRadius: 1 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Typography variant="caption" color="text.secondary">
                          Time Logged
                        </Typography>
                        <Typography variant="body1">
                          {Math.round(project.totalHoursLogged)}h
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              ))}
              {projectReports.length === 0 && (
                <Grid item xs={12}>
                  <Typography color="text.secondary" align="center">
                    No projects found
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </TabPanel>

        {/* Timeline / Gantt Tab */}
        <TabPanel value={tabValue} index={2}>
          <GanttChartSection />
        </TabPanel>
      </Paper>
    </Container>
  );
};

// Gantt Chart Section Component
const GanttChartSection: React.FC = () => {
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [ganttData, setGanttData] = useState<GanttTask[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/projects').then(r => {
      setProjects(r.data);
      if (r.data.length > 0) setSelectedProject(r.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedProject) return;
    setLoading(true);
    getGanttData(selectedProject)
      .then(data => setGanttData(data))
      .catch(() => setGanttData([]))
      .finally(() => setLoading(false));
  }, [selectedProject]);

  // Transform gantt data for horizontal bar chart
  const now = new Date();
  const chartData = ganttData
    .filter(t => t.startDate || t.dueDate)
    .map(t => {
      const start = t.startDate ? parseISO(t.startDate as string) : now;
      const end = t.dueDate ? parseISO(t.dueDate as string) : new Date(start.getTime() + 7 * 86400000);
      const daysFromNow = differenceInDays(start, now);
      const duration = Math.max(1, differenceInDays(end, start));
      return {
        name: t.title.length > 30 ? t.title.substring(0, 30) + '...' : t.title,
        fullTitle: t.title,
        start: Math.max(0, daysFromNow),
        duration,
        progress: t.progress,
        assignee: t.assigneeName || 'Unassigned',
        startDate: format(start, 'MMM d'),
        endDate: format(end, 'MMM d'),
      };
    });

  const getBarColor = (progress: number) => {
    if (progress >= 100) return '#10B981';
    if (progress > 0) return '#3B82F6';
    return '#6B7280';
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Project Timeline</Typography>
        <TextField select size="small" value={selectedProject} label="Project"
          onChange={e => setSelectedProject(e.target.value)} sx={{ minWidth: 200 }}>
          {projects.map((p: any) => (
            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
          ))}
        </TextField>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><LinearProgress sx={{ width: '100%' }} /></Box>
      ) : chartData.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <TimelineIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">No tasks with dates in this project</Typography>
        </Box>
      ) : (
        <Box>
          <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 50)}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20 }}>
              <XAxis type="number" label={{ value: 'Days from today', position: 'bottom' }} />
              <YAxis type="category" dataKey="name" width={200} tick={{ fontSize: 12 }} />
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <Paper sx={{ p: 1.5 }}>
                    <Typography variant="body2" fontWeight="bold">{d.fullTitle}</Typography>
                    <Typography variant="caption" display="block">{d.startDate} - {d.endDate}</Typography>
                    <Typography variant="caption" display="block">Progress: {d.progress}%</Typography>
                    <Typography variant="caption" display="block">Assignee: {d.assignee}</Typography>
                  </Paper>
                );
              }} />
              <Bar dataKey="start" stackId="a" fill="transparent" />
              <Bar dataKey="duration" stackId="a" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={index} fill={getBarColor(entry.progress)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Legend */}
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#10B981', borderRadius: 0.5 }} />
              <Typography variant="caption">Done</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#3B82F6', borderRadius: 0.5 }} />
              <Typography variant="caption">In Progress</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#6B7280', borderRadius: 0.5 }} />
              <Typography variant="caption">Not Started</Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ReportsPage;
